import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast, Toaster } from "sonner";
import { ArrowLeft, ClipboardText } from "@phosphor-icons/react";

export default function AlleyHead() {
  const { id } = useParams();
  const [sessions, setSessions] = useState([]);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/sessions");
      setSessions(data.filter((s) => s.session_status === "active"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadItems = async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/alley-head/sessions/${id}/items`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    loadItems();
  }, [id]);

  const submit = async (item) => {
    const d = drafts[item.id] || {};
    const qty = parseInt(d.counted_quantity || "", 10);

    if (isNaN(qty) || qty < 0) {
      toast.error("Enter a valid count");
      return;
    }

    try {
      await api.post(`/alley-head/sessions/${id}/items/${item.id}/submit`, {
        counted_quantity: qty,
        remarks: d.remarks || "",
      });

      toast.success("Alley count submitted");
      setDrafts((prev) => ({ ...prev, [item.id]: { counted_quantity: "", remarks: "" } }));
      await loadItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusClass = (status) => {
    if (status === "matched") return "text-emerald-700 font-semibold";
    if (status === "short") return "text-red-700 font-semibold";
    if (status === "over") return "text-amber-700 font-semibold";
    return "text-zinc-500 font-semibold";
  };

  if (!id) {
    return (
      <AppShell>
        <Toaster position="top-right" richColors />
        <div className="max-w-4xl mx-auto p-6 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Alley Head</div>
            <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">Alley counting sessions</h1>
            <p className="text-sm text-zinc-600">Select an active session to perform 1st, 2nd, or 3rd count.</p>
          </div>

          <div className="grid gap-3">
            {sessions.map((s) => (
              <Link
                key={s.id}
                to={`/alley-head/sessions/${s.id}`}
                className="rounded-sm border border-zinc-300 bg-white p-4 flex items-center justify-between hover:border-indigo-400"
              >
                <div>
                  <div className="font-display font-semibold text-zinc-900">{s.session_name}</div>
                  <div className="text-xs text-zinc-500 mono">
                    {s.warehouse_name || "—"} · {s.item_count} items
                  </div>
                </div>
                <div className="text-sm text-indigo-900 inline-flex items-center gap-1">
                  Open <ClipboardText className="w-4 h-4" />
                </div>
              </Link>
            ))}

            {sessions.length === 0 && (
              <div className="rounded-sm border border-zinc-300 bg-white p-10 text-center text-zinc-500">
                No active sessions assigned.
              </div>
            )}
          </div>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <Toaster position="top-right" richColors />
        <div className="p-6 text-zinc-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-4">
        <div>
          <Link to="/alley-head" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight mt-1">
            {data.session?.session_name || "Alley Count"}
          </h1>
          <p className="text-sm text-zinc-600">
            System count is hidden. You may count up to 3 times only if not matched.
          </p>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[75vh]">
            <table className="data-table text-xs sm:text-sm">
              <thead className="sticky top-0 bg-white z-10 shadow-sm">
                <tr>
                  <th>Location</th>
                  <th>Item Number</th>
                  <th>Description</th>
                  <th>Batch No</th>
                  <th>Size</th>
                  <th>1st Count</th>
                  <th>2nd Count</th>
                  <th>3rd Count</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th>Input</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {(data.items || []).map((it) => {
                  const d = drafts[it.id] || { counted_quantity: "", remarks: "" };
                  return (
                    <tr key={it.id}>
                      <td className="mono">{it.location || "—"}</td>
                      <td className="mono font-semibold">{it.item_number || "—"}</td>
                      <td className="min-w-[180px]">{it.description || "—"}</td>
                      <td className="mono">{it.batch_no || "—"}</td>
                      <td className="mono">{it.size || "—"}</td>
                      <td className="mono">{it.first_count ?? "—"}</td>
                      <td className="mono">{it.second_count ?? "—"}</td>
                      <td className="mono">{it.third_count ?? "—"}</td>
                      <td className={statusClass(it.status)}>{it.status || "pending"}</td>
                      <td className="text-xs min-w-[260px] max-w-[420px] whitespace-normal break-words leading-relaxed">
                        {it.remarks || "—"}
                      </td>
                      <td>
                        {it.can_submit ? (
                          <div className="space-y-1 min-w-[160px]">
                            <input
                              type="number"
                              min={0}
                              value={d.counted_quantity}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [it.id]: { ...(prev[it.id] || {}), counted_quantity: e.target.value },
                                }))
                              }
                              className="w-full rounded-sm border border-zinc-300 px-2 py-1 mono"
                              placeholder={`Count ${it.attempts_used + 1}`}
                            />
                            <textarea
                              rows={1}
                              value={d.remarks || ""}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [it.id]: { ...(prev[it.id] || {}), remarks: e.target.value },
                                }))
                              }
                              className="w-full rounded-sm border border-zinc-300 px-2 py-1 text-xs"
                              placeholder="Remarks"
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-500">
                            {it.status === "matched" ? "Matched" : "Max attempts"}
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        {it.can_submit && (
                          <button
                            onClick={() => submit(it)}
                            className="rounded-sm bg-indigo-950 hover:bg-indigo-900 text-white px-3 py-1.5 text-xs"
                          >
                            Submit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {(data.items || []).length === 0 && (
                  <tr>
                    <td colSpan={13} className="text-center py-10 text-zinc-500">
                      No items found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
